import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0125 from "../lesson-25/lesson";
const linkLesson0125 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0125.id);

const theory = ` In this Lesson we show how the number of boundary conditions reduces the solution space
<h3 class=sectionHead>Infinite field</h3>
<p>
   In an infinite field any plane wave can propagate without any constraints. Then, it is straightforward to consider progressive plane waves as a basis writing, for any direction:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi mathvariant="bold">n</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">n</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>j</mi>
            <mi mathvariant=bold>k</mi>
            <mo class=MathClass-bin>⋅</mo>
            <mi mathvariant=bold>r</mi>
         </mrow>
      </msup>
   </math>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=bold>k</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>k</mi>
      <mi mathvariant=bold>n</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=bold>n</mi>
   </math>
   any unit vector and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>k</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>. It is also possible to write
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=bold>k</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mo class=MathClass-punc>,</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mo class=MathClass-punc>,</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
   </math>. As
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
      </msub>
   </math>
   are wave number and are usually considered positive, this last expression becomes:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=bold>k</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>(</mo>
      <mo class=MathClass-bin>±</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mo class=MathClass-punc>,</mo>
      <mo class=MathClass-bin>±</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mo class=MathClass-punc>,</mo>
      <mo class=MathClass-bin>±</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
   </math>.
<h3 class=sectionHead>Semi infinite field</h3>
<p>
   In a field limited by an infinite plane perfectly reflecting, the boundary condition constrains the space of solutions. One searches for a basis of solutions compatible with the boundary conditions (see <a href="#" data-img-src=${config.ASSETS_URL}/01/18/theory/figure-1.svg data-img-alt="Figure 1. Semi infinite field.">Figure 1</a>). So, denoting
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
   </math>
   the axis perpendicular to the plane, arbitrarily set at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>, the boundary condition
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi mathvariant=italic>∂p</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>∂x</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </mrow>
         <mrow>
            <mi>x</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   leads to consider waves such as
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>y</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>z</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
            </msub>
            <mi>x</mi>
         </mrow>
      </msup>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∓</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>y</mi>
               </mrow>
            </msub>
            <mi>y</mi>
         </mrow>
      </msup>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∓</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>z</mi>
               </mrow>
            </msub>
            <mi>z</mi>
         </mrow>
      </msup>
   </math>
   as reflections of incident waves such that
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>y</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>z</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
            </msub>
            <mi>x</mi>
         </mrow>
      </msup>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∓</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>y</mi>
               </mrow>
            </msub>
            <mi>y</mi>
         </mrow>
      </msup>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∓</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>z</mi>
               </mrow>
            </msub>
            <mi>z</mi>
         </mrow>
      </msup>
   </math>. It is then straightforward to combine these two solutions in order to obtain a solution compatible with the boundary condition:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∓</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>y</mi>
               </mrow>
            </msub>
            <mi>y</mi>
         </mrow>
      </msup>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∓</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>z</mi>
               </mrow>
            </msub>
            <mi>z</mi>
         </mrow>
      </msup>
   </math>. Note that the basis has twice less functions than in the case of an infinite field.
   <figure class=figure>
<p><img alt="Figure 1. Semi infinite field." src=${config.ASSETS_URL}/01/18/theory/figure-1.svg><figcaption class=caption>Figure 1. Semi infinite field.</figcaption></figure>
<h3 class=sectionHead>Field limited by two and three perpendicular planes</h3>
<p>
   Considering a field limited by two perfectly reflecting planes, with normal
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>y</mi>
   </math>
   and set at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>y</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   respectively (see <a href="#" data-img-src=${config.ASSETS_URL}/01/18/theory/figure-2.svg data-img-alt="Figure 2. space limited by to planes.">Figure 2</a>), the basis of solutions is given by:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=bold>r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mi>y</mi>
      <mo class=MathClass-close>)</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∓</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>z</mi>
               </mrow>
            </msub>
            <mi>z</mi>
         </mrow>
      </msup>
   </math>. The basis has four times less functions than in the case of an infinite field.
   <figure class=figure>
<p><img alt="Figure 2. space limited by to planes." src=${config.ASSETS_URL}/01/18/theory/figure-2.svg><figcaption class=caption>Figure 2. space limited by to planes.</figcaption></figure>
<p>
   For a field limited by three perfectly reflecting planes, with normal
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>y</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>z</mi>
   </math>
   and set at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>y</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>z</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   respectively, the basis of solutions is given by:
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=bold>r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mi>y</mi>
      <mo class=MathClass-close>)</mo>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
      </msub>
      <mi>z</mi>
      <mo class=MathClass-close>)</mo>
   </math>. The basis has height times less functions than in the case of an infinite field.
<h3 class=sectionHead>Plane waveguide</h3>
<p>
   We consider a field limited by two parallel planes perfectly reflecting, with normal
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>–x</mi>
   </math>
   located respectively at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <mi>L</mi>
   </math> (see <a href="#" data-img-src=${config.ASSETS_URL}/01/18/theory/figure-3.svg data-img-alt="Figure 3. Plane wave guide.">Figure 3</a>). This is called a plane waveguide.
   <figure class=figure>
<p><img alt="Figure 3. Plane wave guide." src=${config.ASSETS_URL}/01/18/theory/figure-3.svg><figcaption class=caption>Figure 3. Plane wave guide.</figcaption></figure>
<p>
   The boundary condition at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   determines the choice of the functions basis
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=bold>r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∓</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>y</mi>
               </mrow>
            </msub>
            <mi>y</mi>
         </mrow>
      </msup>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∓</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>z</mi>
               </mrow>
            </msub>
            <mi>z</mi>
         </mrow>
      </msup>
   </math>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-rel>|</mo>
      <mi mathvariant=bold>k</mi>
      <mo class=MathClass-rel>|</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>. The second boundary condition limits the number of solutions since it requires
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mi>L</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   then
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mi>L</mi>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>nπ</mi>
   </math>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>n</mi>
      <mo class=MathClass-rel>∈</mo>
      <mi>ℕ</mi>
   </math>. The solution space is constrained by two conditions
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-rel>|</mo>
      <mi mathvariant=bold>k</mi>
      <mo class=MathClass-rel>|</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>nπ</mi>
         </mrow>
         <mrow>
            <mi>L</mi>
         </mrow>
      </mfrac>
   </math>. If we consider only the waves propagating in the positive
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>y</mi>
   </math>
   direction and therefore such that
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>, the solutions are limited to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=bold>r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>nπx</mi>
         </mrow>
         <mrow>
            <mi>L</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-close>)</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>y</mi>
               </mrow>
            </msub>
            <mi>y</mi>
         </mrow>
      </msup>
   </math>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <msup>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>−</mo>
            <msup>
               <mrow>
                  <mrow>
                     <mo fence=true form=prefix>(</mo>
                     <mrow>
                        <mfrac>
                           <mrow>
                              <mi mathvariant=italic>nπ</mi>
                           </mrow>
                           <mrow>
                              <mi>L</mi>
                           </mrow>
                        </mfrac>
                     </mrow>
                     <mo fence=true form=postfix>)</mo>
                  </mrow>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
      </msqrt>
   </math>. The subscript in the pressure indicates that, for a given frequency, there is a discrete number of solutions.
<p>
   If
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>n</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>, the solution is a plane wave propagating along the
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>y</mi>
   </math>
   axis. Solutions with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>n</mi>
      <mo class=MathClass-rel>></mo>
      <mn>0</mn>
   </math>
   are called higher modes. They are evanescent when
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
      <mo class=MathClass-rel><</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>nc</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mi>L</mi>
         </mrow>
      </mfrac>
   </math>, that is to say that their amplitude decreases exponentially with distance. From a physical point of view these modes result from the superposition of an oblique plane wave and its reflection, the angle
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>𝜃</mi>
   </math>
   being given by
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi class=qopname>tan</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <msup>
               <mrow>
                  <mrow>
                     <mo fence=true form=prefix>(</mo>
                     <mrow>
                        <mfrac>
                           <mrow>
                              <mi mathvariant=italic>kL</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>nπ</mi>
                           </mrow>
                        </mfrac>
                     </mrow>
                     <mo fence=true form=postfix>)</mo>
                  </mrow>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msqrt>
   </math>
   or
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>k</mi>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>.
<p>
   From a practical viewpoint, if
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
      <mo class=MathClass-rel><</mo>
      <mfrac>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mi>L</mi>
         </mrow>
      </mfrac>
   </math>
   (still assuming
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>), only a plane wave progressing along the y axis can propagate. If
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mfrac>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mi>L</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel><</mo>
      <mi>f</mi>
      <mo class=MathClass-rel><</mo>
      <mfrac>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>L</mi>
         </mrow>
      </mfrac>
   </math>
   the mode associated with n = 1 can also propagate. The two modes (plane mode and first higher mode n = 1) then propagate independently at different velocities. Higher modes are dispersive (see <a href="${linkLesson0125}" target="_blank">Lesson 1.25</a>) and propagate with a group velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>
   smaller than the speed of sound and a phase velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mi>ϕ</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
   </math>.
<h3 class=sectionHead>Rectangular waveguide</h3>
<p>
   Now consider a tube limited by 4 perfectly reflecting planes, 2 of normal
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>−</mo>
      <mi>x</mi>
   </math>
   positioned at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
   </math>
   respectively and 2 of normal
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>y</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-bin>−</mo>
      <mi>y</mi>
   </math>
   positioned at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>y</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>y</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
   </math>
   respectively (see <a href="#" data-img-src=${config.ASSETS_URL}/01/18/theory/figure-4.svg data-img-alt="Figure 4. Plane wave guide.">Figure 4</a>). This is called a rectangular waveguide.
   <figure class=figure>
<p><img alt="Figure 4. Plane wave guide." src=${config.ASSETS_URL}/01/18/theory/figure-4.svg><figcaption class=caption>Figure 4. Plane wave guide.</figcaption></figure>
<p>
   The boundary conditions at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>y</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   determines the choice of the functions basis
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=bold>r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mi>y</mi>
      <mo class=MathClass-close>)</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>∓</mo>
            <mi>j</mi>
            <msub>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>z</mi>
               </mrow>
            </msub>
            <mi>z</mi>
         </mrow>
      </msup>
   </math>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-rel>|</mo>
      <mi mathvariant=bold>k</mi>
      <mo class=MathClass-rel>|</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>. The boundary conditions at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>y</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
   </math>
   limits the number of solutions since
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mi>L</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mi>L</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   i.e.
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>nπ</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>mπ</mi>
   </math>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-open>(</mo>
      <mi>n</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>m</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>∈</mo>
      <msup>
         <mrow>
            <mi>ℕ</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>. The solution space is constrained by three conditions
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-rel>|</mo>
      <mi mathvariant=bold>k</mi>
      <mo class=MathClass-rel>|</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>nπ</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>mπ</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>y</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>. For given
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>n</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>m</mi>
   </math>
   we have
</p>
<table class=equation>
   <tr>
      <td style="font-size:11px">
<math display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=bold>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>m</mi>
               </mrow>
            </msub>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>nπx</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>mπy</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>y</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>∓</mo>
                  <mi>j</mi>
                  <msub>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mi>z</mi>
                     </mrow>
                  </msub>
                  <mi>z</mi>
               </mrow>
            </msup>
         </math>
        <td class=eq-no>(1)
   </table>
</math>
<p>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <msup>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>−</mo>
            <msup>
               <mrow>
                  <mrow>
                     <mo fence=true form=prefix>(</mo>
                     <mrow>
                        <mfrac>
                           <mrow>
                              <mi mathvariant=italic>nπ</mi>
                           </mrow>
                           <mrow>
                              <msub>
                                 <mrow>
                                    <mi>L</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>x</mi>
                                 </mrow>
                              </msub>
                           </mrow>
                        </mfrac>
                     </mrow>
                     <mo fence=true form=postfix>)</mo>
                  </mrow>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin>−</mo>
            <msup>
               <mrow>
                  <mrow>
                     <mo fence=true form=prefix>(</mo>
                     <mrow>
                        <mfrac>
                           <mrow>
                              <mi mathvariant=italic>mπ</mi>
                           </mrow>
                           <mrow>
                              <msub>
                                 <mrow>
                                    <mi>L</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>y</mi>
                                 </mrow>
                              </msub>
                           </mrow>
                        </mfrac>
                     </mrow>
                     <mo fence=true form=postfix>)</mo>
                  </mrow>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
      </msqrt>
   </math>. As for the previous example, if
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
      <mo class=MathClass-rel><</mo>
      <mfrac>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
      <mo class=MathClass-rel><</mo>
      <mfrac>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>y</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   only the plane mode
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-open>(</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>,</mo>
      <mn>0</mn>
      <mo class=MathClass-close>)</mo>
   </math>
   is propagating. The other modes are propagating beyond their cut-off frequency given by
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
            <mo class=MathClass-punc>;</mo>
            <mi>n</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>m</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>L</mi>
                           </mrow>
                           <mrow>
                              <mi>x</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>L</mi>
                           </mrow>
                           <mrow>
                              <mi>y</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
   </math>. For a square section guide the normalized cutoff frequencies
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>n</mi>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-punc>,</mo>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   table can be built, and is shown in <a href="#" data-img-src=${config.ASSETS_URL}/01/18/theory/table-1.svg data-img-alt="Table 1. Reduced cut-off frequencies of first modes of a square waveguide. Frequencies are normalized by
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mn>10</mn>
            </mrow>
         </msub>
      </math>.">Table 1</a>.
<div class=table>
   <figure class=float>
      <figcaption class=caption>
         Table 1. Reduced cut-off frequencies of first modes of a square waveguide. Frequencies are normalized by
         <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mn>10</mn>
               </mrow>
            </msub>
         </math>.
      </figcaption>
      <img alt="Table 1. Reduced cut-off frequencies of first modes of a square waveguide. Frequencies are normalized by
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mn>10</mn>
            </mrow>
         </msub>
      </math>." src=${config.ASSETS_URL}/01/18/theory/table-1.svg>
   </figure>
</div>
<p>
   The number
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>n</mi>
   </math>
   determines the number of pressure nodal lines along
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
   </math>
   (thus //
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>y</mi>
   </math>
   ) and the number
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>m</mi>
   </math>
   determines the number of nodal lines along
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>y</mi>
   </math>
   (thus //
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
   </math>
   ). An example is given in <a href="#" data-img-src=${config.ASSETS_URL}/01/18/theory/figure-5.svg data-img-alt="Figure 5. Nodal lines in a square waveguide for modes (0,0) (0,1) (1,0) (1,1) (2,3)">Figure 5</a>.
   <figure class=figure>
<p><img alt="Figure 5. Nodal lines in a square waveguide for modes (0,0) (0,1) (1,0) (1,1) (2,3)." src=${config.ASSETS_URL}/01/18/theory/figure-5.svg><figcaption class=caption>Figure 5. Nodal lines in a square waveguide for modes (0,0) (0,1) (1,0) (1,1) (2,3).</figcaption></figure>
<h3 class=sectionHead>Parallelepipedic cavity</h3>
<p>
   A parallelepipedic is a space limited by 6 planes parallel by pair, each pair being perpendicular to the two others. The boundary conditions at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>y</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>z</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   determine the choice of the functions basis
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mi>x</mi>
      <mo class=MathClass-close>)</mo>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mi>y</mi>
      <mo class=MathClass-close>)</mo>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
      </msub>
      <mi>z</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-rel>|</mo>
      <mi mathvariant="bold">k</mi>
      <mo class=MathClass-rel>|</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>. The boundary conditions at
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>y</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>z</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
      </msub>
   </math>
   limits the number of solutions since
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi class=qopname>sin</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   i.e.
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>nπ</mi>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>y</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>mπ</mi>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi mathvariant=italic>qπ</mi>
   </math>
   with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-open>(</mo>
      <mi>n</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>m</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>q</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>∈</mo>
      <msup>
         <mrow>
            <mi>ℕ</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>. The solution space is constrained by three conditions
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-rel>|</mo>
      <mi mathvariant=bold>k</mi>
      <mo class=MathClass-rel>|</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>x</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>nπ</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>,
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>mπ</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>y</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mi>z</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi mathvariant=italic>qπ</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>z</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>. So solutions basis can be written
</p>
<table class=equation>
   <tr>
      <td style="font-size: 9px">
<math display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>m</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>q</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=bold>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>m</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>q</mi>
               </mrow>
            </msub>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>nπx</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>x</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>mπy</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>y</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>qπz</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                     <mrow>
                        <mi>z</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
         </math>
        <td class=eq-no>(2)
      </mtr>
   </table>
</math>
<p>
   There is no more nonzero solution for all frequencies since
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mo class=MathClass-rel>|</mo>
      <mi mathvariant=bold>k</mi>
      <mo class=MathClass-rel>|</mo>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <msubsup>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>x</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-bin>+</mo>
            <msubsup>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>y</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-bin>+</mo>
            <msubsup>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mi>z</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
         </mrow>
      </msqrt>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>. The frequencies for which there is a non-zero solution are the natural frequencies and the corresponding solutions the eigenmodes of the cavity. These are given by
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
            <mo class=MathClass-punc>;</mo>
            <mi>n</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>m</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>q</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>c</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </mfrac>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>L</mi>
                           </mrow>
                           <mrow>
                              <mi>x</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>m</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>L</mi>
                           </mrow>
                           <mrow>
                              <mi>y</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>q</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>L</mi>
                           </mrow>
                           <mrow>
                              <mi>z</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
   </math>. Note that the number of modes increases as the cube of the frequency. Care must be taken not to confuse the specific modes of a cavity defined by three indices and higher modes of a waveguide which are propagating waves (or not) along the axis of the guide and which are defined by 2 indices.
   <h3 class=sectionHead>References</h3>
  <dl class=thebibliography>
    <dt>[1]
    <dd>L.J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
    <dt>[2]
    <dd>E. Skudrzyk. The Foundations of Acoustics—Basic Mathematics and Basic Acoustics. Springer–Verlag, 1971.
    <dt>[3]
    <dd>S. Temkin. Elements of Acoustics. Wiley, New York, 1981.
`;

export default theory;
